import { cn } from '@/lib/utils';
import { SUPPORTED_LOCALES, useLocale, useTranslation } from '@kgc/i18n';
import { formatCurrency } from '../mock-data';
import { PaymentMethod } from '../types';
import {
  computeConsignmentTotal,
  computeCrossSellGross,
  computeTotalToPay,
} from '../wizard-running-total';
import { useWizardStore } from '../wizard-store';

/**
 * v3-parity G1+G3+G4 — perzisztens felső wizard-chrome.
 *
 * - **G1**: MOST FIZETENDŐ running-total (minden lépésen él, `computeTotalToPay`
 *   SSoT a loyalty-levonással) → a v3-érzet egyik fő eleme.
 * - **G3**: in-wizard HU/EN/DE/UK nyelvváltó. A Header-switcher rejtve marad
 *   (`VITE_I18N_SWITCHER_ENABLED=false`, demo-trust #241), de a wizard kap saját
 *   switchert (Javo G3-döntés (a)); a #241 HU-fallback miatt a hiányzó nem-HU
 *   kulcsok olvasható HU-szöveget adnak, nem törnek.
 * - **G4**: bérlés-azonosító (a wizard-session `draftId` rövidített referenciája).
 */
const LOCALE_LABELS: Record<string, string> = { hu: 'HU', en: 'EN', de: 'DE', uk: 'UK' };

export function WizardTopBar() {
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();

  const draftId = useWizardStore(s => s.draftId);
  const partner = useWizardStore(s => s.partner);
  const cart = useWizardStore(s => s.cart);
  const costBearerId = useWizardStore(s => s.costBearerId);
  const pricing = useWizardStore(s => s.pricing);
  const loyaltyRedeem = useWizardStore(s => s.loyaltyRedeem);
  const crossSellItems = useWizardStore(s => s.crossSellItems);
  // v3-paritás #2 (ctxbar-delta): fizetésmód + tartozék-összeg a labeled strip-hez.
  const paymentMethod = useWizardStore(s => s.paymentMethod);
  const accessoryCatalogQtys = useWizardStore(s => s.accessoryCatalogQtys);
  // consignment-0 reconcile: a CONSIGNMENT package-tételek a Footer-rel egyező SSoT-ból.
  const packageClassifiedItems = useWizardStore(s => s.packageClassifiedItems);
  const consignmentQuantities = useWizardStore(s => s.consignmentQuantities);

  const totalToPay = computeTotalToPay({
    grandTotal: pricing?.grandTotal ?? 0,
    depositTotal: pricing?.depositTotal ?? 0,
    crossSellGross: computeCrossSellGross(crossSellItems).gross,
    consignmentTotal: computeConsignmentTotal(packageClassifiedItems, consignmentQuantities),
    loyaltyAmount: Number(loyaltyRedeem?.amount ?? 0),
  });

  // P1 context-infosáv: Bérlő → Költségviselő · gépdarabszám.
  const machineCount = (cart ?? []).reduce((sum, item) => sum + item.quantity, 0);
  const isOtherCostBearer = !!costBearerId && costBearerId !== partner?.id;

  // Tartozék-összeg az összes gép katalógus-qty-jából (proto ctxbar „N tartozék").
  let accCount = 0;
  if (accessoryCatalogQtys) {
    for (const rec of accessoryCatalogQtys.values()) {
      for (const entry of Object.values(rec)) accCount += entry.qty;
    }
  }
  // Fizetésmód-címke (proto ctxbar „MOST FIZETENDŐ · <mód>").
  const payLabelKey =
    paymentMethod === PaymentMethod.CARD
      ? 'rental.wizard.ctx.payCard'
      : paymentMethod === PaymentMethod.CASH
        ? 'rental.wizard.ctx.payCash'
        : paymentMethod === PaymentMethod.TRANSFER
          ? 'rental.wizard.ctx.payTransfer'
          : null;

  return (
    <div
      className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--border-secondary,#e2e8f0)] bg-[var(--accent-soft,#e6f4f1)] px-4 py-2 sm:px-6 lg:px-8"
      data-testid="wizard-top-bar"
    >
      {/* v3-paritás #2: címkézett ctxbar — BÉRLŐ / GÉPEK·tartozék / (jobb) MOST FIZETENDŐ·fizetésmód. */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-1">
        {/* BÉRLŐ */}
        {partner && (
          <div className="flex min-w-0 flex-col gap-0.5" data-testid="wizard-top-bar-renter-item">
            <span
              className="text-[10px] font-semibold uppercase tracking-wide text-[var(--accent,#0f766e)]"
              data-testid="wizard-top-bar-renter-label"
            >
              {t('rental.wizard.ctx.renter')}
            </span>
            <div
              className="flex flex-wrap items-center gap-x-1.5 text-xs"
              data-testid="wizard-top-bar-context"
            >
              <span className="truncate font-medium text-foreground">{partner.name}</span>
              {isOtherCostBearer && partner.costBearerName && (
                <span className="text-muted-foreground">→ {partner.costBearerName}</span>
              )}
            </div>
          </div>
        )}

        {/* GÉPEK · tartozék */}
        {(machineCount > 0 || accCount > 0) && (
          <div className="flex flex-col gap-0.5" data-testid="wizard-top-bar-machines-item">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--accent,#0f766e)]">
              {t('rental.wizard.ctx.machines')}
            </span>
            <div className="flex items-center gap-1.5 text-xs text-foreground">
              <span>{t('rental.wizard.equipmentCount', { count: machineCount })}</span>
              {accCount > 0 && (
                <span className="text-muted-foreground" data-testid="wizard-top-bar-acc-count">
                  · {t('rental.wizard.ctx.accCount', { count: accCount })}
                </span>
              )}
            </div>
          </div>
        )}

        {/* bérlés-ID */}
        <span className="text-[11px] text-muted-foreground" data-testid="wizard-top-bar-rental-id">
          {t('rental.wizard.draftRef')} #{draftId.slice(0, 8)}
        </span>
      </div>

      <div className="flex items-center gap-4">
        <div className="text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--accent,#0f766e)]">
            {t('rental.wizard.totalToPay')}
          </div>
          <div className="flex items-center justify-end gap-1.5">
            <span
              className="text-lg font-bold tabular-nums text-foreground"
              data-testid="wizard-top-bar-total"
            >
              {formatCurrency(totalToPay)}
            </span>
            {payLabelKey && (
              <span
                className="text-[11px] text-muted-foreground"
                data-testid="wizard-top-bar-pay-method"
              >
                · {t(payLabelKey)}
              </span>
            )}
          </div>
        </div>

        <div
          className="flex items-center gap-0.5 rounded-md border border-input p-0.5"
          role="group"
        >
          {SUPPORTED_LOCALES.map(loc => (
            <button
              key={loc}
              type="button"
              onClick={() => setLocale(loc)}
              className={cn(
                'rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors',
                locale === loc
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent'
              )}
              data-testid={`wizard-lang-${loc}`}
              aria-pressed={locale === loc}
            >
              {LOCALE_LABELS[loc] ?? loc.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
